import classNames from "classnames";
import type { ReactNode } from "react";
import type { CSSProperties } from "styled-components";
import { useTimelineCtx, type ITimelineCtx } from "../ctx";
import { TimelineStyleEnum } from "../types";
import { TimelinePathSinusoid } from "./timeline-path-sinusoid";

interface IProps {
  children: ReactNode;
}

export const TimelineItemsWrapper = ({ children }: IProps) => {
  const ctx = useTimelineCtx();
  const { isHorizontal, totalAmtOfItems } = ctx;

  const contentPlacement = ctx.comboSettings.timelineContentPlacement;
  const isAlternating = isHorizontal && contentPlacement === "alternating";
  const isPath =
    isHorizontal && ctx.comboSettings.timelineStyle === TimelineStyleEnum.Path;
  const needsStretch =
    isHorizontal &&
    (contentPlacement === "alternating" || contentPlacement === "split");

  return (
    <div
      className={classNames({
        "tw-w-full [align-items:inherit] [justify-content:inherit]":
          !needsStretch,
        "tw-w-full !tw-items-stretch [justify-content:inherit]": needsStretch,
        "tw-grid tw-justify-start tw-will-change-transform": isHorizontal,
      })}
      style={{
        ...makeStyles(ctx),
        ...(isAlternating && {
          gridTemplateRows: "auto auto auto",
          alignItems: "stretch",
        }),
      }}
    >
      {isAlternating && isPath && (
        <div
          className="tw-relative tw-overflow-hidden"
          style={{
            gridColumn: `1 / -1`,
            gridRow: 2,
            height: "100%",
            pointerEvents: "none",
          }}
        >
          <TimelinePathSinusoid itemCount={totalAmtOfItems} firstItemIdx={0} />
        </div>
      )}
      {children}
    </div>
  );
};

const makeStyles = (ctx: ITimelineCtx): CSSProperties | undefined => {
  if (!ctx.isHorizontal) return undefined;
  const { isMobile, isPdf, totalAmtOfItems, itemsInView, itemsMovedBy } = ctx;

  const hasTransform = !(isMobile || isPdf);
  const paddingX = hasTransform ? 64 : 0;
  const gapVar = "var(--timeline-items-gap)";

  return {
    width: `calc(100% - ${gapVar} * ${itemsInView - 1} - ${paddingX * 2}px)`,
    marginLeft: paddingX,
    gridTemplateColumns: `repeat(${totalAmtOfItems}, calc(100% / ${itemsInView}))`,
    columnGap: gapVar,
    transform: hasTransform
      ? `translateX(calc((100% / ${itemsInView} + ${gapVar}) * ${-itemsMovedBy}))`
      : "none",
    transition: hasTransform ? "transform 500ms ease" : "none",
  };
};
